'use strict';

/**
 * ImageZoom — opens a fullscreen modal with a zoomable/pannable product image.
 *
 * Triggered by buttons/links with [data-zoom-src] attribute.
 * Modal selectors: .zoom-modal, .zoom-modal__overlay, .zoom-modal__img, .zoom-modal__close
 *
 * Features:
 *  - Keyboard: Escape to close, arrow keys to pan
 *  - Mouse drag to pan when zoomed
 *  - Double-click / scroll wheel to zoom in/out
 *  - Touch pinch-to-zoom + drag support
 *  - Close on overlay click or close button
 */
(function () {
  // ---- State ----------------------------------------------------------------
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const SCALE_STEP = 0.4;

  let currentScale = 1;
  let translateX = 0;
  let translateY = 0;
  let isDragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragOriginX = 0;
  let dragOriginY = 0;

  // Touch pinch state
  let lastPinchDistance = 0;

  // ---- DOM ------------------------------------------------------------------
  let modal = null;
  let overlay = null;
  let img = null;
  let closeBtn = null;

  function getOrCreateModal() {
    modal = document.querySelector('.zoom-modal');
    if (modal) {
      overlay = modal.querySelector('.zoom-modal__overlay');
      img = modal.querySelector('.zoom-modal__img');
      closeBtn = modal.querySelector('.zoom-modal__close');
      return;
    }

    modal = document.createElement('div');
    modal.className = 'zoom-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Image zoom');
    modal.innerHTML = `
      <div class="zoom-modal__overlay"></div>
      <div class="zoom-modal__content">
        <img class="zoom-modal__img" src="" alt="" draggable="false">
      </div>
      <button class="zoom-modal__close" aria-label="Close zoom">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    `;
    document.body.appendChild(modal);

    overlay = modal.querySelector('.zoom-modal__overlay');
    img = modal.querySelector('.zoom-modal__img');
    closeBtn = modal.querySelector('.zoom-modal__close');
  }

  // ---- Transform ------------------------------------------------------------
  function applyTransform() {
    img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${currentScale})`;
  }

  function resetTransform() {
    currentScale = 1;
    translateX = 0;
    translateY = 0;
    applyTransform();
  }

  function clampTranslate() {
    if (currentScale <= 1) {
      translateX = 0;
      translateY = 0;
      return;
    }
    const imgRect = img.getBoundingClientRect();
    const viewW = window.innerWidth;
    const viewH = window.innerHeight;
    const maxX = Math.max(0, (imgRect.width - viewW) / 2);
    const maxY = Math.max(0, (imgRect.height - viewH) / 2);
    translateX = Math.max(-maxX, Math.min(maxX, translateX));
    translateY = Math.max(-maxY, Math.min(maxY, translateY));
  }

  // ---- Open / Close ---------------------------------------------------------
  function openModal(src, alt) {
    getOrCreateModal();
    resetTransform();
    img.src = src;
    img.alt = alt || '';
    modal.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    closeBtn.focus();
    bindEvents();
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    img.src = '';
    resetTransform();
    unbindEvents();
  }

  // ---- Events ---------------------------------------------------------------
  function onKeyDown(e) {
    if (e.key === 'Escape') { closeModal(); return; }
    if (e.key === '+' || e.key === '=') { zoomIn(); return; }
    if (e.key === '-') { zoomOut(); return; }
    if (e.key === 'ArrowLeft')  { translateX += 20; clampTranslate(); applyTransform(); }
    if (e.key === 'ArrowRight') { translateX -= 20; clampTranslate(); applyTransform(); }
    if (e.key === 'ArrowUp')    { translateY += 20; clampTranslate(); applyTransform(); }
    if (e.key === 'ArrowDown')  { translateY -= 20; clampTranslate(); applyTransform(); }
  }

  function onWheel(e) {
    e.preventDefault();
    const delta = e.deltaY < 0 ? SCALE_STEP : -SCALE_STEP;
    currentScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, currentScale + delta));
    clampTranslate();
    applyTransform();
  }

  function onMouseDown(e) {
    if (e.button !== 0) return;
    isDragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    dragOriginX = translateX;
    dragOriginY = translateY;
    img.style.cursor = 'grabbing';
    e.preventDefault();
  }

  function onMouseMove(e) {
    if (!isDragging) return;
    translateX = dragOriginX + (e.clientX - dragStartX);
    translateY = dragOriginY + (e.clientY - dragStartY);
    clampTranslate();
    applyTransform();
  }

  function onMouseUp() {
    isDragging = false;
    img.style.cursor = currentScale > 1 ? 'grab' : 'zoom-in';
  }

  function onDblClick() {
    if (currentScale > 1) {
      resetTransform();
    } else {
      currentScale = 2;
    }
    img.style.cursor = currentScale > 1 ? 'grab' : 'zoom-in';
    clampTranslate();
    applyTransform();
  }

  // Touch events
  function getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function onTouchStart(e) {
    if (e.touches.length === 2) {
      lastPinchDistance = getTouchDistance(e.touches);
    } else if (e.touches.length === 1) {
      isDragging = true;
      dragStartX = e.touches[0].clientX;
      dragStartY = e.touches[0].clientY;
      dragOriginX = translateX;
      dragOriginY = translateY;
    }
  }

  function onTouchMove(e) {
    e.preventDefault();
    if (e.touches.length === 2) {
      const dist = getTouchDistance(e.touches);
      const scaleChange = (dist - lastPinchDistance) / 120;
      currentScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, currentScale + scaleChange));
      lastPinchDistance = dist;
      clampTranslate();
      applyTransform();
    } else if (e.touches.length === 1 && isDragging) {
      translateX = dragOriginX + (e.touches[0].clientX - dragStartX);
      translateY = dragOriginY + (e.touches[0].clientY - dragStartY);
      clampTranslate();
      applyTransform();
    }
  }

  function onTouchEnd() {
    isDragging = false;
  }

  function bindEvents() {
    document.addEventListener('keydown', onKeyDown);
    img.parentElement.addEventListener('wheel', onWheel, { passive: false });
    img.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    img.addEventListener('dblclick', onDblClick);
    img.parentElement.addEventListener('touchstart', onTouchStart, { passive: true });
    img.parentElement.addEventListener('touchmove', onTouchMove, { passive: false });
    img.parentElement.addEventListener('touchend', onTouchEnd);
    overlay.addEventListener('click', closeModal);
    closeBtn.addEventListener('click', closeModal);
  }

  function unbindEvents() {
    document.removeEventListener('keydown', onKeyDown);
    if (img && img.parentElement) {
      img.parentElement.removeEventListener('wheel', onWheel);
      img.parentElement.removeEventListener('touchstart', onTouchStart);
      img.parentElement.removeEventListener('touchmove', onTouchMove);
      img.parentElement.removeEventListener('touchend', onTouchEnd);
    }
    if (img) {
      img.removeEventListener('mousedown', onMouseDown);
      img.removeEventListener('dblclick', onDblClick);
    }
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    if (overlay) overlay.removeEventListener('click', closeModal);
    if (closeBtn) closeBtn.removeEventListener('click', closeModal);
  }

  // ---- Helpers --------------------------------------------------------------
  function zoomIn() {
    currentScale = Math.min(MAX_SCALE, currentScale + SCALE_STEP);
    clampTranslate();
    applyTransform();
  }

  function zoomOut() {
    currentScale = Math.max(MIN_SCALE, currentScale - SCALE_STEP);
    clampTranslate();
    applyTransform();
  }

  // ---- Init -----------------------------------------------------------------
  function init() {
    // Delegate click on any [data-zoom-src] element
    document.addEventListener('click', function (e) {
      const trigger = e.target.closest('[data-zoom-src]');
      if (!trigger) return;
      e.preventDefault();
      const src = trigger.dataset.zoomSrc;
      const alt = trigger.dataset.zoomAlt || (trigger.querySelector('img') || {}).alt || '';
      if (src) openModal(src, alt);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose public API for programmatic use
  window.Visionary = window.Visionary || {};
  window.Visionary.ImageZoom = { open: openModal, close: closeModal };
})();
